<template>
  <div class="animated fadeIn salary-config-page">
    <div class="card">
      <div class="main-card-body">
        <div class="body-header">
          <div class="salary-config-title-wrap">
            <h4>Cấu hình tính lương</h4>
            <el-select
              :value="selectedContractTypeId"
              filterable
              class="salary-config-contract-select"
              :style="{ width: contractTypeSelectWidth }"
              placeholder="Chọn loại hợp đồng"
              @change="handleContractTypeChange"
            >
              <el-option
                v-for="item in contractTypes"
                :key="item.id"
                :label="item.name"
                :value="item.id"
              ></el-option>
            </el-select>
          </div>

          <div class="salary-config-toolbar__actions">
            <el-button class="salary-config-toolbar__btn" @click="toggleSearchBox">
              Ẩn/hiện tìm kiếm
            </el-button>
            <el-button class="salary-config-toolbar__btn" @click="goToFormulaSetup">
              Thiết lập công thức
            </el-button>
            <el-button class="salary-config-toolbar__btn" @click="applyFormulaConfigs">
              Áp dụng
            </el-button>
          </div>
        </div>

        <b-collapse :visible="showSearchBox" id="salaryConfigSearchPanel">
          <div class="salary-config-search-panel">
            <div class="salary-config-search-field">
              <label>Nhóm công thức</label>
              <el-select
                v-model="searchGroupCode"
                clearable
                filterable
                class="salary-config-search-control"
                placeholder="Tất cả nhóm công thức"
              >
                <el-option label="Tất cả" value=""></el-option>
                <el-option
                  v-for="item in salaryConfigGroupOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
            </div>

            <div class="salary-config-search-field">
              <label>Tên cột</label>
              <el-select
                v-model="searchColumnCode"
                clearable
                filterable
                class="salary-config-search-control"
                placeholder="Tất cả tên cột"
              >
                <el-option label="Tất cả" value=""></el-option>
                <el-option
                  v-for="item in salaryConfigColumnOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                ></el-option>
              </el-select>
            </div>

            <div class="salary-config-search-field salary-config-search-field--keyword">
              <label>Tìm kiếm</label>
              <el-input
                v-model="keyword"
                clearable
                class="salary-config-search-control"
                placeholder="Nhập tên cột hoặc mã cột"
              >
                <i slot="prefix" class="el-input__icon el-icon-search"></i>
              </el-input>
            </div>
          </div>
        </b-collapse>

        <div class="main-body-table">
          <el-table
            ref="formulaTable"
            :data="filteredColumns"
            border
            fit
            class="salary-config-table"
            :row-class-name="getRowClassName"
            :header-cell-style="getHeaderCellStyle"
            :cell-style="getCellStyle"
            :span-method="getSpanMethod"
            @row-click="handleRowClick"
          >
            <el-table-column
              prop="displayName"
              label="Tên cột trên giao diện"
              min-width="180"
            >
              <template slot-scope="scope">
                <button
                  v-if="scope.row.isGroup"
                  type="button"
                  class="salary-config-group-toggle"
                  @click.stop="toggleSalaryConfigGroup(scope.row.groupCode)"
                >
                  <i
                    :class="
                      isSalaryConfigGroupExpanded(scope.row.groupCode)
                        ? 'el-icon-caret-bottom'
                        : 'el-icon-caret-right'
                    "
                  ></i>
                  <span>{{ scope.row.displayName }}</span>
                </button>
                <span v-else>{{ scope.row.displayName }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="code" label="Mã cột" min-width="200">
              <template slot-scope="scope">
                <span>{{ getVisibleCode(scope.row) }}</span>
              </template>
            </el-table-column>
            <el-table-column
              prop="formula"
              label="Công thức tính"
              min-width="420"
              class-name="salary-config-formula-column"
            >
              <template slot-scope="scope">
                <span class="formula-text">{{ getVisibleFormula(scope.row) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="description" label="Diễn giải" min-width="360">
              <template slot-scope="scope">
                <span class="description-text">
                  <span
                    v-for="(line, lineIndex) in getVisibleDescriptionLines(scope.row)"
                    :key="lineIndex"
                    class="description-line"
                  >{{ line }}</span>
                </span>
              </template>
            </el-table-column>
            <el-table-column
              label="Thao tác"
              width="80"
              align="center"
              class-name="salary-config-action-column"
            >
              <template slot-scope="scope">
                <el-button
                  v-if="canEditFormula(scope.row)"
                  size="mini"
                  plain
                  round
                  type="primary"
                  class="salary-config-edit-btn"
                  title="Sửa công thức"
                  @click="goToFormulaSetup(scope.row)"
                >
                  <i class="far fa-edit"></i>
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <el-empty
            v-if="!filteredColumns.length"
            description="Không có công thức phù hợp với từ khóa tìm kiếm"
          ></el-empty>

          <div class="salary-config-table-footer">
            <el-button class="salary-config-toolbar__btn" @click="goToConfigHistory">
              Lịch sử cấu hình tính lương
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ALL_CONTRACT_TYPE_FORMULA_KEY,
  buildSalaryFormulaApiPayload,
  getDefaultSalaryFormulaColumns,
  mapSalaryFormulaApiItemsToConfigs,
  SALARY_FORMULA_GROUPS,
} from "../shared/salaryFormulaConfig";
import ContractTypeApi from "../../../../moduleApi/ContractTypeApi";
import SalaryApi from "../../../../moduleApi/SalaryApi";
import SalaryFormulaConfigApi from "../../../../moduleApi/SalaryFormulaConfigApi";

const FORMULA_FUNCTION_LABELS = {
  FLOOR: "lay_phan_nguyen",
  MAX: "gia_tri_lon_nhat",
  MIN: "gia_tri_nho_nhat",
  TAXRATE: "thue_suat",
};

const SALARY_CONFIG_GROUPS = SALARY_FORMULA_GROUPS;

export default {
  data() {
    return {
      keyword: "",
      showSearchBox: false,
      searchGroupCode: "",
      searchColumnCode: "",
      selectedContractTypeId: ALL_CONTRACT_TYPE_FORMULA_KEY,
      formulaColumns: [],
      contractTypes: [],
      salaryConfigExpandedGroups: SALARY_CONFIG_GROUPS.reduce((result, item) => {
        result[item.code] = true;
        return result;
      }, {}),
    };
  },

  computed: {
    orderedFormulaColumns() {
      return [...this.formulaColumns];
    },

    salaryConfigRows() {
      const groupMap = SALARY_CONFIG_GROUPS.reduce((result, item) => {
        result[item.code] = item.label;
        return result;
      }, {});
      let currentGroupCode = "";

      return this.orderedFormulaColumns.reduce((result, item) => {
        if (groupMap[item.code]) {
          currentGroupCode = item.code;
          result.push({
            isGroup: true,
            code: `group_${item.code}`,
            groupCode: item.code,
            displayName: groupMap[item.code],
          });
        }

        if (!currentGroupCode || this.isSalaryConfigGroupExpanded(currentGroupCode)) {
          result.push(item);
        }

        return result;
      }, []);
    },

    salaryConfigColumnGroups() {
      const groupMap = SALARY_CONFIG_GROUPS.reduce((result, item) => {
        result[item.code] = item.label;
        return result;
      }, {});
      let currentGroupCode = "";

      return this.orderedFormulaColumns.reduce((result, item) => {
        if (groupMap[item.code]) {
          currentGroupCode = item.code;
        }
        result[item.code] = currentGroupCode;
        return result;
      }, {});
    },

    salaryConfigGroupOptions() {
      return SALARY_CONFIG_GROUPS.map((item) => ({
        value: item.code,
        label: item.label,
      }));
    },

    salaryConfigColumnOptions() {
      return this.orderedFormulaColumns.map((item) => ({
        value: item.code,
        label: item.displayName,
      }));
    },

    filteredColumns() {
      const keyword = this.normalizeText(this.keyword);
      const groupCode = this.searchGroupCode || "";
      const columnCode = this.searchColumnCode || "";

      if (!keyword && !groupCode && !columnCode) {
        return this.salaryConfigRows;
      }

      const matchesColumnFilters = (item) => {
        if (!item || item.isGroup) {
          return false;
        }

        if (groupCode && this.salaryConfigColumnGroups[item.code] !== groupCode) {
          return false;
        }

        if (columnCode && item.code !== columnCode) {
          return false;
        }

        return true;
      };

      const matchesKeyword = (item) => {
        if (!keyword) {
          return true;
        }
        const haystack = [
          item.codeLabel,
          item.displayName,
        ].join(" ");
        return this.normalizeText(haystack).includes(keyword);
      };

      const matchedGroupCodes = new Set(
        this.orderedFormulaColumns
          .filter((item) => matchesColumnFilters(item) && matchesKeyword(item))
          .map((item) => this.salaryConfigColumnGroups[item.code])
          .filter(Boolean)
      );

      return this.salaryConfigRows.filter((item) => {
        if (item.isGroup) {
          return !columnCode && matchedGroupCodes.has(item.groupCode);
        }

        return matchesColumnFilters(item) && matchesKeyword(item);
      });
    },

    selectedContractTypeLabel() {
      if (this.selectedContractTypeId === ALL_CONTRACT_TYPE_FORMULA_KEY) {
        return "Tất cả loại hợp đồng";
      }
      const contractType = this.contractTypes.find((item) => item.id === this.selectedContractTypeId);
      return contractType && contractType.name ? contractType.name : "Chọn loại hợp đồng";
    },

    contractTypeSelectWidth() {
      const textLength = `${this.selectedContractTypeLabel || ""}`.length;
      const width = textLength * 9 + 40;
      return `${Math.min(Math.max(width, 200), 420)}px`;
    },
  },

  async mounted() {
    await Promise.all([
      this.loadContractTypes(),
      this.loadFormulaConfigs(),
    ]);
    this.formulaColumns = this.normalizeFormulaColumns(this.formulaColumns);
  },

  methods: {
    async loadContractTypes() {
      try {
        const response = await ContractTypeApi.list({ limit: 1000000, filters: "disabled!=true" }, true);
        this.contractTypes = Array.isArray(response && response.result) ? response.result : [];
      } catch (error) {
        this.contractTypes = [];
      }

      this.selectedContractTypeId = this.getSelectedContractTypeFromRoute();
    },

    getSelectedContractTypeFromRoute() {
      const requestedContractTypeId = `${(this.$route.query && this.$route.query.contractTypeId) || ""}`.trim();
      if (requestedContractTypeId === ALL_CONTRACT_TYPE_FORMULA_KEY) {
        return requestedContractTypeId;
      }

      const matchedContractType = this.contractTypes.find((item) => item && item.id === requestedContractTypeId);
      if (matchedContractType) {
        return requestedContractTypeId;
      }

      return this.contractTypes.length > 0 ? this.contractTypes[0].id : ALL_CONTRACT_TYPE_FORMULA_KEY;
    },

    async loadFormulaConfigs() {
      try {
        const response = await SalaryFormulaConfigApi.list({ limit: 1000000 }, true);
        const apiConfigs = Array.isArray(response && response.result) ? response.result : [];
        const normalizedConfigs = mapSalaryFormulaApiItemsToConfigs(apiConfigs);
        this.formulaColumns = this.normalizeFormulaColumns(normalizedConfigs);
      } catch (error) {
        this.formulaColumns = this.normalizeFormulaColumns(getDefaultSalaryFormulaColumns());
      }
    },

    handleContractTypeChange(contractTypeId) {
      this.selectedContractTypeId = contractTypeId || ALL_CONTRACT_TYPE_FORMULA_KEY;
      this.formulaColumns = this.normalizeFormulaColumns(this.formulaColumns);
      this.$router
        .replace({
          name: this.$route.name,
          query: {
            ...this.$route.query,
            contractTypeId: this.selectedContractTypeId,
          },
        })
        .catch(() => {});
    },

    toggleSearchBox() {
      this.showSearchBox = !this.showSearchBox;
    },

    async applyFormulaConfigs() {
      try {
        await Promise.all(
          this.formulaColumns.map((item, index) => {
            const payload = buildSalaryFormulaApiPayload(item, index);
            return payload.id
              ? SalaryFormulaConfigApi.update(payload)
              : SalaryFormulaConfigApi.create(payload);
          })
        );
        await this.saveFormulaConfigHistory();

        await this.loadFormulaConfigs();
        if (window.toastr) {
          window.toastr.success("Áp dụng công thức tính lương thành công");
        }
      } catch (error) {
        if (window.toastr) {
          window.toastr.error("Không đồng bộ được công thức lên máy chủ.");
        }
      }
    },

    async saveFormulaConfigHistory() {
      const now = new Date();
      await SalaryApi.SaveFormulaConfigHistory({
        versionName: `Cấu hình tính lương ${now.toLocaleString("vi-VN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}`,
        configs: this.formulaColumns.map((item) => ({
          id: item.id,
          code: item.code,
          codeLabel: item.codeLabel,
          displayName: item.displayName,
          formula: item.formula || "",
          description: item.description || "",
          contractTypeFormulas: item.contractTypeFormulas || {},
          sortOrder: item.sortOrder,
          groupKey: item.groupKey,
          groupName: item.groupName,
          isActive: item.isActive !== false,
          disabled: item.disabled === true,
        })),
        contractTypes: this.contractTypes.map((item) => ({
          id: item.id,
          name: item.name,
        })),
      });
    },

    isSalaryConfigGroupExpanded(groupCode) {
      return this.salaryConfigExpandedGroups[groupCode] !== false;
    },

    toggleSalaryConfigGroup(groupCode) {
      if (!groupCode) {
        return;
      }
      this.$set(
        this.salaryConfigExpandedGroups,
        groupCode,
        !this.isSalaryConfigGroupExpanded(groupCode)
      );
    },

    handleRowClick(row) {
      if (row && row.isGroup) {
        this.toggleSalaryConfigGroup(row.groupCode);
        return;
      }
    },

    goToFormulaSetup(row = null) {
      const currentRow =
        row ||
        this.formulaColumns.find((item) => this.canEditFormula(item));

      if (!currentRow) {
        if (window.toastr) {
          window.toastr.warning("Không có công thức phù hợp để thiết lập");
        }
        return;
      }

      this.$router
        .push({
          name: "Admin-Salary-SalaryConfig-FormulaSetup",
          query: {
            code: currentRow.code,
            contractTypeId: this.selectedContractTypeId,
          },
        })
        .catch(() => {});
    },

    goToConfigHistory() {
      this.$router
        .push({ name: "Admin-Salary-SalaryConfig-History" })
        .catch(() => {});
    },

    normalizeText(value) {
      return `${value || ""}`
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },

    getRowClassName({ row }) {
      if (row && row.isGroup) {
        return "salary-config-row--group";
      }
      return "";
    },

    getHeaderCellStyle() {
      return {
        background: "#f89b56",
        color: "#ffffff",
        fontWeight: "700",
      };
    },

    getCellStyle({ column }) {
      if (column.property === "formula") {
        return {
          verticalAlign: "middle",
        };
      }
      if (column.property === "description") {
        return {
          verticalAlign: "top",
        };
      }
      return {};
    },

    getSpanMethod({ row, columnIndex }) {
      if (!row || !row.isGroup) {
        return {
          rowspan: 1,
          colspan: 1,
        };
      }

      if (columnIndex === 0) {
        return {
          rowspan: 1,
          colspan: 5,
        };
      }

      return {
        rowspan: 0,
        colspan: 0,
      };
    },

    getFormulaLabelByCode(code, columns = this.formulaColumns) {
      const foundItem = (columns || []).find((item) => item.code === code);
      return foundItem && foundItem.displayName ? foundItem.displayName : code;
    },

    getFormulaCodeDisplay(code, columns = this.formulaColumns) {
      const foundItem = (columns || []).find((item) => item.code === code);
      return foundItem && foundItem.codeLabel ? foundItem.codeLabel : code;
    },

    getVisibleCode(row) {
      if (!row) {
        return "";
      }
      if (row.isGroup) {
        return "";
      }
      return row.codeLabel || row.code || "";
    },

    canEditFormula(row) {
      if (row && row.isGroup) {
        return false;
      }
      return !!`${this.getFormulaByContractType(row) || ""}`.trim();
    },

    getVisibleFormula(row) {
      return this.canEditFormula(row)
        ? this.formatFormulaDisplay(this.getFormulaByContractType(row), this.formulaColumns, false)
        : "";
    },

    getVisibleDescription(row) {
      if (!row || row.isGroup) {
        return "";
      }

      const formula = this.getFormulaByContractType(row);
      const fallbackDescription = row.baseDescription || row.description || "";
      return this.buildFormulaDescription(
        formula,
        row.displayName,
        fallbackDescription,
        this.formulaColumns
      );
    },

    getVisibleDescriptionLines(row) {
      return this.splitDescriptionLines(this.getVisibleDescription(row));
    },

    splitDescriptionLines(description) {
      const normalizedDescription = `${description || ""}`.trim();

      if (!normalizedDescription) {
        return [];
      }

      return normalizedDescription
        .replace(/\s+-\s+/g, "\n- ")
        .replace(/\s+Trong\s+/g, "\nTrong ")
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean);
    },

    getFormulaByContractType(row, contractTypeId = this.selectedContractTypeId) {
      if (!row) {
        return "";
      }

      const contractTypeFormulas =
        row.contractTypeFormulas && typeof row.contractTypeFormulas === "object"
          ? row.contractTypeFormulas
          : {};

      if (contractTypeId && contractTypeFormulas[contractTypeId] !== undefined) {
        return contractTypeFormulas[contractTypeId];
      }

      if (contractTypeFormulas[ALL_CONTRACT_TYPE_FORMULA_KEY] !== undefined) {
        return contractTypeFormulas[ALL_CONTRACT_TYPE_FORMULA_KEY];
      }

      return row.formula || "";
    },

    buildFormulaDescription(
      formula,
      displayName,
      fallbackDescription = "",
      columns = this.formulaColumns
    ) {
      const normalizedFormula = `${formula || ""}`.trim();
      const normalizedDisplayName = `${displayName || ""}`.trim();

      if (!normalizedFormula) {
        return `${fallbackDescription || ""}`.trim();
      }

      const specialDescription = this.buildSpecialFormulaDescription(
        normalizedFormula,
        normalizedDisplayName,
        columns
      );
      if (specialDescription) {
        return specialDescription;
      }

      const humanReadableFormula = this.formatFormulaDisplay(
        normalizedFormula,
        columns,
        true
      );
      const readableFormula = this.formatReadableDescriptionFormula(humanReadableFormula);

      if (!normalizedDisplayName) {
        return readableFormula;
      }

      return readableFormula.includes("\n")
        ? `[${normalizedDisplayName}] =\n${readableFormula}.`
        : `[${normalizedDisplayName}] = ${readableFormula}.`;
    },

    buildSpecialFormulaDescription(formula, displayName, columns = this.formulaColumns) {
      const normalizedFormula = `${formula || ""}`.replace(/\s+/g, "");
      const hasToken = (code) => {
        const foundItem = (columns || []).find((item) => item.code === code);
        const tokenCodes = [code, foundItem && foundItem.codeLabel].filter(Boolean);
        return tokenCodes.some((tokenCode) => normalizedFormula.includes(`[${tokenCode}]`));
      };
      const isOtFormula =
        hasToken("ot_150") &&
        hasToken("ot_200") &&
        hasToken("ot_300") &&
        hasToken("salary_level") &&
        hasToken("standard_work");

      if (!isOtFormula || !this.isStandardOtFormula(normalizedFormula, columns)) {
        return "";
      }

      const otName = displayName || "OT";
      const ot150Label = this.getFormulaLabelByCode("ot_150", columns);
      const ot200Label = this.getFormulaLabelByCode("ot_200", columns);
      const ot300Label = this.getFormulaLabelByCode("ot_300", columns);
      const salaryLabel = this.getFormulaLabelByCode("salary_level", columns);
      const standardWorkLabel = this.getFormulaLabelByCode("standard_work", columns);

      return [
        `[${otName}] = tiền OT 150% + tiền OT 200% + tiền OT 300%.`,
        `- Tiền OT 150% = ${ot150Label} * Đơn giá giờ * 1.5`,
        `- Tiền OT 200% = ${ot200Label} * Đơn giá giờ * 2`,
        `- Tiền OT 300% = ${ot300Label} * Đơn giá giờ * 3`,
        `Trong đó: Đơn giá giờ = ${salaryLabel} / ${standardWorkLabel} / 8.`,
      ].join("\n");
    },

    isStandardOtFormula(normalizedFormula, columns = this.formulaColumns) {
      const terms = `${normalizedFormula || ""}`
        .split("+")
        .map((term) => term.trim())
        .filter(Boolean);

      if (terms.length !== 3) {
        return false;
      }

      const hasTokenInTerm = (term, code) => {
        const foundItem = (columns || []).find((item) => item.code === code);
        const tokenCodes = [code, foundItem && foundItem.codeLabel].filter(Boolean);
        return tokenCodes.some((tokenCode) => term.includes(`[${tokenCode}]`));
      };
      const hasRateInTerm = (term, percentRate, decimalRate) =>
        term.includes(`*${percentRate}`) || term.includes(`*${decimalRate}`);
      const termMatches = (code, percentRate, decimalRate) =>
        terms.some(
          (term) => hasTokenInTerm(term, code) && hasRateInTerm(term, percentRate, decimalRate)
        );

      return (
        termMatches("ot_150", "150%", "1.5") &&
        termMatches("ot_200", "200%", "2") &&
        termMatches("ot_300", "300%", "3")
      );
    },

    formatReadableDescriptionFormula(formulaText) {
      const normalizedText = `${formulaText || ""}`.replace(/\s+/g, " ").trim();
      const shouldSplit =
        normalizedText.length > 90 ||
        (normalizedText.match(/\s[+-]\s/g) || []).length > 1;

      if (!shouldSplit) {
        return normalizedText;
      }

      return normalizedText
        .replace(/\s\+\s/g, "\n+ ")
        .replace(/\s-\s/g, "\n- ")
        .trim();
    },

    formatFormulaDisplay(formula, columns = this.formulaColumns, useLabels = false) {
      let formattedFormula = `${formula || ""}`.trim();
      const formulaCodeItems = (columns || [])
        .map((item) => item && item.code)
        .filter(Boolean)
        .sort((first, second) => second.length - first.length);

      formattedFormula = formattedFormula.replace(/\[([^\]]+)\]/g, (_, token) => {
        const normalizedToken = token.trim();
        const displayToken = useLabels
          ? this.getFormulaLabelByCode(normalizedToken, columns)
          : this.getFormulaCodeDisplay(normalizedToken, columns);
        return `[${displayToken}]`;
      });

      formulaCodeItems.forEach((code) => {
        const escapedCode = code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        formattedFormula = formattedFormula.replace(
          new RegExp(`(^|[^A-Za-z0-9_\\]])(${escapedCode})(?=[^A-Za-z0-9_]|$)`, "g"),
          (match, prefix, token) => {
            const displayToken = useLabels
              ? this.getFormulaLabelByCode(token, columns)
              : this.getFormulaCodeDisplay(token, columns);
            return `${prefix}[${displayToken}]`;
          }
        );
      });

      Object.keys(FORMULA_FUNCTION_LABELS).forEach((functionName) => {
        formattedFormula = formattedFormula.replace(
          new RegExp(`\\b${functionName}\\b`, "g"),
          FORMULA_FUNCTION_LABELS[functionName]
        );
      });

      return formattedFormula
        .replace(/[()]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    },

    normalizeFormulaColumns(columns) {
      const normalizedColumns = columns || [];
      return normalizedColumns.map((item) => {
        const formula = this.getFormulaByContractType(item);
        const fallbackDescription = item.baseDescription || item.description;
        return {
          ...item,
          baseDescription: fallbackDescription,
          description: this.buildFormulaDescription(
            formula,
            item.displayName,
            fallbackDescription,
            normalizedColumns
          ),
        };
      });
    },
  },
};
</script>

<style scoped>
.salary-config-page {
  padding-right: 20px;
}

.salary-config-page .main-card-body {
  padding-left: 22px;
  padding-right: 22px;
}

.salary-config-page .body-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-left: -20px;
  margin-right: -20px;
  margin-bottom: 14px;
}

.salary-config-page .body-header h4 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

.salary-config-title-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.salary-config-contract-select {
  max-width: 100%;
  transition: width 0.18s ease;
}

.salary-config-toolbar__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

::v-deep .salary-config-toolbar__btn {
  min-width: 104px;
  color: #ffffff;
  background: #f89b56;
  border-color: #f89b56;
}

::v-deep .salary-config-toolbar__btn:hover,
::v-deep .salary-config-toolbar__btn:focus,
::v-deep .salary-config-toolbar__btn:active {
  color: #ffffff;
  background: #f58b3d;
  border-color: #f58b3d;
}

.salary-config-search-panel {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(260px, 1fr) minmax(420px, 2fr);
  gap: 22px 32px;
  align-items: center;
  padding: 8px 0 24px;
}

.salary-config-search-field {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  min-width: 0;
}

.salary-config-search-field label {
  margin: 0;
  color: #606266;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.salary-config-search-control {
  width: 100%;
}

.main-body-table {
  border: none;
  border-radius: 0;
  overflow: visible;
}

.salary-config-table-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 14px;
}

.formula-text {
  display: inline-block;
  white-space: pre-line;
  line-height: 1.4;
  color: #606266;
}

.description-text {
  display: inline-block;
  white-space: normal;
  line-height: 1.4;
  color: #606266;
}

.description-line {
  display: block;
  line-height: 1.4;
}

.salary-config-group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: #303133;
  font: inherit;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.salary-config-group-toggle i {
  flex: 0 0 auto;
  color: #303133;
  font-size: 13px;
}

.salary-config-group-toggle:focus {
  outline: none;
}

::v-deep .salary-config-table th > .cell {
  font-size: 14px;
  line-height: 1.3;
  color: #ffffff;
  user-select: text;
  -webkit-user-select: text;
}

::v-deep .salary-config-table,
::v-deep .salary-config-table::before,
::v-deep .salary-config-table .el-table__body-wrapper,
::v-deep .salary-config-table .el-table__fixed::before,
::v-deep .salary-config-table .el-table__fixed-right::before {
  border: none !important;
}

::v-deep .salary-config-table .el-table__body td {
  padding: 6px 0;
}

::v-deep .salary-config-table .el-table__body .cell {
  font-size: 14px;
  color: #606266;
  line-height: 1.3;
  word-break: break-word;
}

::v-deep .salary-config-table .salary-config-formula-column {
  vertical-align: middle;
}

::v-deep .salary-config-table .salary-config-formula-column .cell {
  display: flex;
  align-items: center;
  min-height: 100%;
}

::v-deep .salary-config-table .el-table__body tr:hover > td {
  background: #f8fbff !important;
}

::v-deep .salary-config-table .salary-config-row--group > td {
  padding: 8px 0;
  background: #ffffff !important;
  font-weight: 700;
}

::v-deep .salary-config-table .salary-config-row--group .cell {
  color: #303133;
  font-weight: 700;
}

::v-deep .salary-config-table .salary-config-row--group:hover > td {
  background: #ffffff !important;
}

@media (max-width: 1200px) {
  .salary-config-search-panel {
    grid-template-columns: repeat(2, minmax(260px, 1fr));
  }

  .salary-config-search-field--keyword {
    grid-column: 1 / -1;
  }
}

@media (max-width: 992px) {
  .salary-config-page {
    padding-right: 0;
  }

  .salary-config-page .main-card-body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .salary-config-page .body-header {
    flex-direction: column;
    align-items: stretch;
    margin-left: 0;
    margin-right: 0;
  }

  .salary-config-title-wrap {
    flex-wrap: wrap;
  }

  .salary-config-toolbar__actions {
    width: 100%;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .salary-config-search-panel {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .salary-config-search-field {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}
</style>
